import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Bot, Cpu, Loader2, CheckCircle2, XCircle, Play, Square, Zap, Send,
  RefreshCw, Sparkles, Eye, Trash2, RotateCcw, ChevronLeft, ChevronRight,
  X, Settings, ThumbsUp, FileText, Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Model Status Panel ───

function ModelPanel({ status, onRefresh, onLoad, onUnload, loading }) {
  if (!status) return null;

  return (
    <Card className="mb-6">
      <CardContent className="py-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <div className={`rounded-lg p-2 ${status.online ? 'bg-green-100' : 'bg-red-100'}`}>
              <Cpu className={`w-5 h-5 ${status.online ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Ollama {status.online ? 'Online' : 'Offline'}
              </p>
              <p className="text-xs text-gray-400 truncate">{status.host}</p>
            </div>
            {status.online && (
              <div className="flex gap-2 flex-wrap">
                {status.loaded_models?.length > 0 ? (
                  status.loaded_models.map((m, i) => (
                    <Badge key={i} className="bg-green-100 text-green-700 gap-1 max-w-[240px]">
                      <Zap className="w-3 h-3 shrink-0" />
                      <span className="truncate">{m.name} ({m.size_vram_gb}GB VRAM)</span>
                    </Badge>
                  ))
                ) : (
                  <Badge variant="outline" className="text-gray-400">No models loaded</Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap justify-end min-w-0">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {status.available_models?.map(m => {
              const isLoaded = status.loaded_models?.some(l => l.name === m.name);
              if (m.family === 'nomic-bert') return null;
              return (
                <Button
                  key={m.name}
                  size="sm"
                  variant={isLoaded ? 'destructive' : 'default'}
                  onClick={() => isLoaded ? onUnload(m.name) : onLoad(m.name)}
                  disabled={loading}
                  className="gap-1 max-w-full"
                  title={isLoaded ? `Unload ${m.name}` : `Load ${m.name}`}
                >
                  {isLoaded ? <Square className="w-3 h-3 shrink-0" /> : <Play className="w-3 h-3 shrink-0" />}
                  <span className="truncate">{isLoaded ? `Unload ${m.name}` : `Load ${m.name}`}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Email Preview Card ───

function EmailPreview({ email, onApprove, onRegenerate, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  const statusConfig = {
    generated: { color: 'bg-blue-100 text-blue-700', icon: Sparkles },
    approved: { color: 'bg-green-100 text-green-700', icon: ThumbsUp },
    sent: { color: 'bg-purple-100 text-purple-700', icon: Send },
    failed: { color: 'bg-red-100 text-red-700', icon: XCircle },
  };
  const sc = statusConfig[email.status] || statusConfig.generated;
  const StatusIcon = sc.icon;

  return (
    <div className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900">{email.contact_name}</span>
            <Badge className={`${sc.color} text-[10px] gap-0.5`}><StatusIcon className="w-2.5 h-2.5" />{email.status}</Badge>
            <span className="text-[10px] text-gray-400">{email.model_used} · {email.generation_time_ms}ms</span>
          </div>
          <p className="text-xs text-gray-500">{email.contact_title} at {email.contact_company}</p>
          <p className="text-xs text-blue-600">{email.contact_email}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          {email.status === 'generated' && (
            <>
              <Button size="sm" variant="outline" onClick={() => onApprove(email.id)} className="gap-1 h-7 text-xs">
                <ThumbsUp className="w-3 h-3" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onRegenerate(email.id)} className="h-7 text-xs">
                <RotateCcw className="w-3 h-3" />
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(email.id)} className="h-7 text-xs text-red-400 hover:text-red-600">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <p className="text-sm font-medium text-gray-800 mb-1">Subject: {email.generated_subject}</p>
        {expanded ? (
          <p className="text-sm text-gray-600 whitespace-pre-line">{email.generated_body}</p>
        ) : (
          <p className="text-sm text-gray-500 truncate">{email.generated_body?.split('\n')[0]}</p>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───

export default function EmailPersonalizePage() {
  const [ollamaStatus, setOllamaStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  // Generate form
  const [contactId, setContactId] = useState('');
  const [selectedModel, setSelectedModel] = useState('qwen3.5:35b-a3b');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [generating, setGenerating] = useState(false);

  // Batch form
  const [showBatch, setShowBatch] = useState(false);
  const [batchContactIds, setBatchContactIds] = useState('');
  const [batchCampaignName, setBatchCampaignName] = useState('');
  const [batchJob, setBatchJob] = useState(null);
  const [batchPolling, setBatchPolling] = useState(false);

  // Template editor
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', system_prompt: '', user_prompt_template: '', temperature: 0.7, max_tokens: 512 });

  const LIMIT = 20;

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/email-personalize/ollama-status`, { withCredentials: true });
      setOllamaStatus(data);
    } catch { setOllamaStatus({ online: false, host: 'unknown' }); }
    setStatusLoading(false);
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/email-personalize/templates`, { withCredentials: true });
      setTemplates(data);
    } catch {}
  }, []);

  const fetchEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({ skip: page * LIMIT, limit: LIMIT });
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API}/api/email-personalize/emails?${params}`, { withCredentials: true });
      setEmails(data.emails);
      setEmailsTotal(data.total);
    } catch {}
  }, [page, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/email-personalize/stats`, { withCredentials: true });
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); fetchTemplates(); fetchStats(); }, []);
  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const refreshAll = () => { fetchStatus(); fetchEmails(); fetchStats(); fetchTemplates(); };

  const loadModel = async (model) => {
    setStatusLoading(true);
    try {
      await axios.post(`${API}/api/email-personalize/ollama-load`, { model, keep_alive: '30m' }, { withCredentials: true });
      toast.success(`${model} loaded`);
    } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
    fetchStatus();
  };

  const unloadModel = async (model) => {
    setStatusLoading(true);
    try {
      await axios.post(`${API}/api/email-personalize/ollama-unload`, { model }, { withCredentials: true });
      toast.success(`${model} unloaded`);
    } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
    fetchStatus();
  };

  const generateSingle = async () => {
    if (!contactId.trim()) { toast.error('Enter a contact ID'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/email-personalize/generate`, {
        contact_id: contactId.trim(), model: selectedModel,
        template_id: selectedTemplate || undefined, temperature,
      }, { withCredentials: true });
      toast.success('Email generated!');
      setContactId('');
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const startBatch = async () => {
    const ids = batchContactIds.split(/[,\n\s]+/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) { toast.error('Enter contact IDs'); return; }
    try {
      const { data } = await axios.post(`${API}/api/email-personalize/batch`, {
        contact_ids: ids, model: selectedModel, temperature,
        template_id: selectedTemplate || undefined,
        campaign_name: batchCampaignName, concurrency: 1,
      }, { withCredentials: true });
      setBatchJob(data);
      toast.success(`Batch started: ${ids.length} contacts`);
      pollBatch(data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Batch failed'); }
  };

  const pollBatch = async (jobId) => {
    setBatchPolling(true);
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/email-personalize/batch/${jobId}`, { withCredentials: true });
        setBatchJob(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
          setBatchPolling(false);
          refreshAll();
          if (data.status === 'completed') toast.success(`Batch done: ${data.completed} generated`);
        }
      } catch { clearInterval(interval); setBatchPolling(false); }
    }, 3000);
  };

  const approveEmail = async (id) => {
    await axios.post(`${API}/api/email-personalize/emails/${id}/approve`, {}, { withCredentials: true });
    toast.success('Approved');
    fetchEmails(); fetchStats();
  };

  const bulkApprove = async () => {
    const ids = emails.filter(e => e.status === 'generated').map(e => e.id);
    if (!ids.length) { toast.error('No emails to approve'); return; }
    await axios.post(`${API}/api/email-personalize/emails/bulk-approve`, ids, { withCredentials: true });
    toast.success(`${ids.length} emails approved`);
    fetchEmails(); fetchStats();
  };

  const regenerateEmail = async (id) => {
    try {
      await axios.post(`${API}/api/email-personalize/emails/${id}/regenerate`, {
        contact_id: '', model: selectedModel, temperature,
      }, { withCredentials: true });
      toast.success('Regenerated');
      fetchEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteEmail = async (id) => {
    await axios.delete(`${API}/api/email-personalize/emails/${id}`, { withCredentials: true });
    toast.success('Deleted');
    fetchEmails(); fetchStats();
  };

  const saveTemplate = async () => {
    try {
      if (editTemplate) {
        await axios.put(`${API}/api/email-personalize/templates/${editTemplate.id}`, templateForm, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/email-personalize/templates`, templateForm, { withCredentials: true });
        toast.success('Template created');
      }
      setShowTemplateEditor(false);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const totalPages = Math.ceil(emailsTotal / LIMIT);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-purple-100 rounded-lg p-2"><Wand2 className="w-6 h-6 text-purple-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Personalization</h1>
            <p className="text-sm text-gray-500">AI-powered personalized emails via Ollama (Qwen3)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditTemplate(null); setTemplateForm({ name: '', system_prompt: '', user_prompt_template: '', temperature: 0.7, max_tokens: 512 }); setShowTemplateEditor(true); }}>
            <FileText className="w-4 h-4 mr-1" /> Templates
          </Button>
          <Button variant="outline" size="sm" onClick={refreshAll}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Model Status */}
      <ModelPanel status={ollamaStatus} onRefresh={fetchStatus} onLoad={loadModel} onUnload={unloadModel} loading={statusLoading} />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-bold">{stats.total || 0}</p>
          <p className="text-xs text-gray-500">Total Generated</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.generated || 0}</p>
          <p className="text-xs text-gray-500">Pending Review</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.approved || 0}</p>
          <p className="text-xs text-gray-500">Approved</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-bold text-gray-500">{stats.avg_generation_ms ? `${(stats.avg_generation_ms / 1000).toFixed(1)}s` : '—'}</p>
          <p className="text-xs text-gray-500">Avg Gen Time</p>
        </CardContent></Card>
      </div>

      {/* Generate Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Single Generate */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Generate Single Email</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Contact ID (from Email List)</Label>
              <Input value={contactId} onChange={e => setContactId(e.target.value)} placeholder="Paste contact ID..." className="h-9" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Model</Label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="w-full text-sm border rounded-lg px-2 py-1.5">
                  <option value="qwen3.5:35b-a3b">Qwen3.5 35B MoE (default)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Temperature: {temperature}</Label>
                <input type="range" min="0.1" max="1.0" step="0.1" value={temperature} onChange={e => setTemperature(parseFloat(e.target.value))} className="w-full" />
              </div>
            </div>
            {templates.length > 0 && (
              <div>
                <Label className="text-xs mb-1 block">Prompt Template</Label>
                <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} className="w-full text-sm border rounded-lg px-2 py-1.5">
                  <option value="">Default template</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <Button onClick={generateSingle} disabled={generating || !contactId.trim()} className="w-full gap-2 bg-purple-600 hover:bg-purple-700">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Personalized Email
            </Button>
          </CardContent>
        </Card>

        {/* Batch Generate */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Batch Generate</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Campaign Name</Label>
              <Input value={batchCampaignName} onChange={e => setBatchCampaignName(e.target.value)} placeholder="e.g., Q2 Recruiter Outreach" className="h-9" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Contact IDs (one per line or comma-separated)</Label>
              <textarea value={batchContactIds} onChange={e => setBatchContactIds(e.target.value)}
                placeholder="Paste contact IDs here..." rows={4}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono resize-y" />
            </div>
            <Button onClick={startBatch} disabled={batchPolling || !batchContactIds.trim()} className="w-full gap-2 bg-purple-600 hover:bg-purple-700">
              {batchPolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              {batchPolling ? `Generating... ${batchJob?.completed || 0}/${batchJob?.total || 0}` : 'Start Batch Generation'}
            </Button>
            {batchJob && (
              <div className={`text-xs p-2 rounded-lg ${batchJob.status === 'completed' ? 'bg-green-50 text-green-700' : batchJob.status === 'running' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'}`}>
                {batchJob.status === 'running' && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                    <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${batchJob.total > 0 ? (batchJob.completed / batchJob.total) * 100 : 0}%` }} />
                  </div>
                )}
                {batchJob.completed}/{batchJob.total} generated · {batchJob.failed} failed · {batchJob.status}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Queue */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Generated Emails ({emailsTotal})</CardTitle>
            <div className="flex gap-2">
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                className="text-sm border rounded-lg px-2 py-1 bg-white">
                <option value="">All</option>
                <option value="generated">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
              </select>
              {emails.some(e => e.status === 'generated') && (
                <Button size="sm" onClick={bulkApprove} className="gap-1 bg-green-600 hover:bg-green-700">
                  <ThumbsUp className="w-3 h-3" /> Approve All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {emails.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No generated emails yet. Generate one above to get started.</p>
          ) : (
            emails.map(e => (
              <EmailPreview key={e.id} email={e} onApprove={approveEmail} onRegenerate={regenerateEmail} onDelete={deleteEmail} />
            ))
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-3">
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTemplateEditor(false)}>
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{editTemplate ? 'Edit Template' : 'New Prompt Template'}</CardTitle>
                <button onClick={() => setShowTemplateEditor(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm mb-1 block">Template Name</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Cold Outreach - Recruiters" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">System Prompt</Label>
                <textarea value={templateForm.system_prompt} onChange={e => setTemplateForm(f => ({ ...f, system_prompt: e.target.value }))}
                  rows={6} className="w-full px-3 py-2 border rounded-lg text-sm font-mono resize-y" />
              </div>
              <div>
                <Label className="text-sm mb-1 block">User Prompt Template</Label>
                <p className="text-[10px] text-gray-400 mb-1">Available: {'{{first_name}} {{last_name}} {{title}} {{company}} {{city}} {{state}} {{sender_name}} {{sender_company}} {{sender_product}} {{sender_value_prop}}'}</p>
                <textarea value={templateForm.user_prompt_template} onChange={e => setTemplateForm(f => ({ ...f, user_prompt_template: e.target.value }))}
                  rows={8} className="w-full px-3 py-2 border rounded-lg text-sm font-mono resize-y" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Temperature: {templateForm.temperature}</Label>
                  <input type="range" min="0.1" max="1.0" step="0.1" value={templateForm.temperature} onChange={e => setTemplateForm(f => ({ ...f, temperature: parseFloat(e.target.value) }))} className="w-full" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Max Tokens</Label>
                  <Input type="number" value={templateForm.max_tokens} onChange={e => setTemplateForm(f => ({ ...f, max_tokens: parseInt(e.target.value) || 512 }))} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveTemplate} className="flex-1">{editTemplate ? 'Update' : 'Create'} Template</Button>
                <Button variant="outline" onClick={() => setShowTemplateEditor(false)}>Cancel</Button>
              </div>

              {templates.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-xs text-gray-500 mb-2">Existing Templates</p>
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <span className="text-sm text-gray-800">{t.name}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditTemplate(t); setTemplateForm({ name: t.name, system_prompt: t.system_prompt, user_prompt_template: t.user_prompt_template, temperature: t.temperature, max_tokens: t.max_tokens }); }}
                          className="h-7 text-xs">Edit</Button>
                        <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/email-personalize/templates/${t.id}`, { withCredentials: true }); fetchTemplates(); toast.success('Deleted'); }}
                          className="h-7 text-xs text-red-400">Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
